import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type {
  AdminBaremeDTO,
  AdminEasyshopBoutiqueDTO,
  AdminEcritureDTO,
  AdminEcritureResumeDTO,
  AdminHiboutikAccountDTO,
  AdminInitialisationDTO,
  AdminInitialisationFileDTO,
  AdminInitialisationResumeDTO,
  AdminRecapitulatifDTO,
} from "@easyshop/shared";
import {
  Button,
  Card,
  Drawer,
  DrawerHeader,
  Empty,
  ErrorState,
  FieldLabel,
  Loading,
  Pill,
  SectionTitle,
  StatCard,
  Tabs,
  cx,
  inputClass,
} from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { api } from "@/lib/api";
import { num, relative } from "@/lib/format";
import {
  useAdminEasyshopReseaux,
  usePlatformBoutiques,
  useAdminEasyshopBoutiques,
  useAdminHiboutikAccounts,
  useAdminInitialisation,
  useAdminInitialisations,
  useAdminStatus,
} from "@/lib/queries";

type Onglet = "initialisation" | "caisse";

/** Dernière initialisation suivie sur ce poste : on la retrouve après un rechargement. */
const CLE_TRAVAIL = "easyshop.admin.initialisation";

function lireTravail(): string | null {
  try {
    return localStorage.getItem(CLE_TRAVAIL);
  } catch {
    return null;
  }
}
function garderTravail(id: string | null) {
  try {
    if (id) localStorage.setItem(CLE_TRAVAIL, id);
    else localStorage.removeItem(CLE_TRAVAIL);
  } catch {
    /* stockage indisponible : le suivi tient le temps de la page */
  }
}

/**
 * Les corrections de la grille et les barèmes retouchés d'un lancement : gardés
 * sur ce poste, par lancement, pour qu'un rechargement ne les efface pas. La
 * caisse, elle, garde le lancement — pas ce qu'on en a corrigé avant d'écrire.
 */
const cleRetouches = (id: string) => `${CLE_TRAVAIL}.${id}.retouches`;

function lireRetouches(id: string): { corrections: Corrections; baremes: Bareme[] | null } {
  try {
    const brut = localStorage.getItem(cleRetouches(id));
    if (!brut) return { corrections: CORRECTIONS_VIDES, baremes: null };
    const lu = JSON.parse(brut) as { corrections?: Partial<Corrections>; baremes?: Bareme[] | null };
    return { corrections: { ...CORRECTIONS_VIDES, ...lu.corrections }, baremes: Array.isArray(lu.baremes) ? lu.baremes : null };
  } catch {
    return { corrections: CORRECTIONS_VIDES, baremes: null };
  }
}

function garderRetouches(id: string, corrections: Corrections, baremes: Bareme[] | null) {
  try {
    localStorage.setItem(cleRetouches(id), JSON.stringify({ corrections, baremes }));
  } catch {
    /* stockage indisponible : les retouches tiennent le temps de la page */
  }
}

/** Les retouches des lancements que la caisse ne garde plus n'ont plus d'objet. */
function oublierRetouchesSauf(ids: string[]) {
  try {
    const gardes = new Set(ids.map(cleRetouches));
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const cle = localStorage.key(i);
      if (cle?.startsWith(`${CLE_TRAVAIL}.`) && cle.endsWith(".retouches") && !gardes.has(cle)) localStorage.removeItem(cle);
    }
  } catch {
    /* rien à nettoyer */
  }
}

/**
 * Administration plateforme (administrateurs plateforme uniquement) :
 * initialisation d'une boutique depuis Hiboutik, à blanc puis (plus tard) en
 * écriture, et liaison des boutiques de la caisse Easy Shop au tableau de bord.
 */
export function AdministrationPage() {
  const { me } = useSession();
  const [onglet, setOnglet] = useState<Onglet>("initialisation");
  const status = useAdminStatus();
  if (!me.platformAdmin) return <Navigate to="/" replace />;
  const s = status.data;

  return (
    <div className="flex flex-col gap-[18px]">
      <Card pad="px-5 py-4" className="flex flex-wrap items-center gap-3">
        <span
          className={cx(
            "h-[10px] w-[10px] rounded-full",
            status.isLoading ? "bg-track" : s?.reachable ? "bg-positive" : "bg-danger"
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-bold">Caisse Easy Shop</div>
          <div className="text-[12px] text-muted-2">
            {status.isLoading
              ? "Vérification de la connexion…"
              : s?.reachable
                ? `Connectée (${s.url}) · ${s.linkedBoutiques} boutique${s.linkedBoutiques > 1 ? "s" : ""} alimentée${s.linkedBoutiques > 1 ? "s" : ""} par la caisse`
                : (s?.error ?? "Connexion impossible")}
          </div>
        </div>
        <Button variant="outline" onClick={() => void status.refetch()}>
          Vérifier
        </Button>
      </Card>

      <Tabs<Onglet>
        tabs={[
          { key: "initialisation", label: "Initialiser une boutique" },
          { key: "caisse", label: "Boutiques de la caisse" },
        ]}
        value={onglet}
        onChange={setOnglet}
      />

      {onglet === "initialisation" ? <Initialisation disponible={!!s?.reachable} /> : <BoutiquesCaisse disponible={!!s?.reachable} />}
    </div>
  );
}

// --- Initialisation à blanc --------------------------------------------------

function Initialisation({ disponible }: { disponible: boolean }) {
  const accounts = useAdminHiboutikAccounts();
  const [travailId, setTravailId] = useState<string | null>(() => lireTravail());
  const travail = useAdminInitialisation(travailId);
  const [accountId, setAccountId] = useState("");
  const [siren, setSiren] = useState("");
  const [magasins, setMagasins] = useState("");
  const [depuis, setDepuis] = useState("");
  const [moisActifs, setMoisActifs] = useState("12");
  const [moisArrivages, setMoisArrivages] = useState("12");
  const [ouvrirFiches, setOuvrirFiches] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const precedents = useAdminInitialisations(disponible);

  useEffect(() => garderTravail(travailId), [travailId]);
  // Initialisation que la caisse ne garde plus (au-delà d'une semaine) : on l'oublie.
  useEffect(() => {
    if (travail.error) setTravailId(null);
  }, [travail.error]);
  useEffect(() => {
    if (precedents.data) oublierRetouchesSauf(precedents.data.map((p) => p.id));
  }, [precedents.data]);

  const account = accounts.data?.find((a) => a.id === accountId) ?? null;
  const parReseau = useMemo(() => {
    const m = new Map<string, AdminHiboutikAccountDTO[]>();
    for (const a of accounts.data ?? []) m.set(a.organization.name, [...(m.get(a.organization.name) ?? []), a]);
    return [...m.entries()];
  }, [accounts.data]);

  const lancer = async () => {
    setBusy(true);
    setErr(null);
    try {
      const ids = magasins
        .split(/[\s,;]+/)
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0);
      const t = await api.post<AdminInitialisationDTO>("/platform/administration/initialisations", {
        hiboutikAccountId: accountId,
        options: {
          siren: siren.trim() || null,
          magasins: ids.length ? ids : null,
          depuis: depuis || null,
          moisActifs: Number(moisActifs) || 0,
          moisArrivages: Number(moisArrivages) || 0,
          ouvrirFiches,
        },
      });
      setTravailId(t.id);
      void precedents.refetch();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  const enCours = travail.data?.statut === "EN_COURS";

  return (
    <div className="flex flex-col gap-[18px]">
      <Card className="flex flex-col gap-4">
        <div>
          <SectionTitle>Initialisation à blanc depuis Hiboutik</SectionTitle>
          <div className="mt-1 text-[12.5px] leading-[1.5] text-muted">
            La caisse Easy Shop lit le compte Hiboutik choisi — magasins, catalogue, stock, clients, vendeurs, remises,
            paiements, historique des ventes — et prépare la reprise. <b>Rien n&apos;est écrit</b>, ni chez Hiboutik (verrou
            de lecture seule) ni dans la caisse. Les fichiers obtenus s&apos;affichent ci-dessous et se téléchargent pour
            un tableur.
          </div>
        </div>

        {accounts.isLoading && <Loading />}
        {accounts.error && <ErrorState error={accounts.error} />}
        {accounts.data && (
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
            <label className="col-span-full">
              <FieldLabel>Compte Hiboutik</FieldLabel>
              <select className={inputClass} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">Choisir un compte…</option>
                {parReseau.map(([reseau, list]) => (
                  <optgroup key={reseau} label={reseau}>
                    {list.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label} — {a.subdomain}.hiboutik.com ({a.boutiques.length} boutique{a.boutiques.length > 1 ? "s" : ""})
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {account && (
                <div className="mt-[6px] text-[11.5px] text-muted-2">
                  Boutiques du tableau de bord sur ce compte :{" "}
                  {account.boutiques.length
                    ? account.boutiques.map((b) => `${b.city}${b.shopId != null ? ` (magasin ${b.shopId})` : ""}`).join(", ")
                    : "aucune"}
                </div>
              )}
            </label>
            <label>
              <FieldLabel>SIREN de la société</FieldLabel>
              <input className={inputClass} value={siren} onChange={(e) => setSiren(e.target.value)} placeholder="9 chiffres — identité légale" />
            </label>
            <label>
              <FieldLabel>Magasins Hiboutik</FieldLabel>
              <input className={inputClass} value={magasins} onChange={(e) => setMagasins(e.target.value)} placeholder="Tous, ou ex. 1, 3" />
            </label>
            <label>
              <FieldLabel>Historique depuis</FieldLabel>
              <input className={inputClass} type="month" value={depuis} onChange={(e) => setDepuis(e.target.value)} />
              <div className="mt-[6px] text-[11.5px] text-muted-2">Vide : depuis la première vente du magasin.</div>
            </label>
            <label>
              <FieldLabel>Clients actifs sur (mois)</FieldLabel>
              <input className={inputClass} type="number" min={0} max={240} value={moisActifs} onChange={(e) => setMoisActifs(e.target.value)} />
              <div className="mt-[6px] text-[11.5px] text-muted-2">0 : tous les clients.</div>
            </label>
            <label>
              <FieldLabel>Produits arrivés sur (mois)</FieldLabel>
              <input className={inputClass} type="number" min={0} max={240} value={moisArrivages} onChange={(e) => setMoisArrivages(e.target.value)} />
              <div className="mt-[6px] text-[11.5px] text-muted-2">Un produit encore en stock est toujours repris. 0 : tous les produits.</div>
            </label>
            <label className="col-span-full flex items-start gap-2 text-[13px]">
              <input type="checkbox" className="mt-[3px]" checked={ouvrirFiches} onChange={(e) => setOuvrirFiches(e.target.checked)} />
              <span>
                Lire aussi les soldes et statuts de fidélité
                <span className="block text-[11.5px] text-muted-2">Une requête Hiboutik par client repris : nettement plus long.</span>
              </span>
            </label>
          </div>
        )}

        {err != null && <ErrorState error={err} />}
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => void lancer()} disabled={!accountId || busy || enCours || !disponible}>
            {busy ? "Lancement…" : "Lancer à blanc"}
          </Button>
          {!disponible && <span className="text-[12px] text-danger">Caisse Easy Shop injoignable : voir l&apos;état en haut de page.</span>}
          {travailId && !enCours && (
            <Button variant="ghost" onClick={() => setTravailId(null)}>
              Effacer le résultat
            </Button>
          )}
        </div>
      </Card>

      {precedents.data && precedents.data.length > 0 && (
        <LancementsPrecedents lancements={precedents.data} courant={travailId} onReprendre={setTravailId} />
      )}

      {travailId && travail.isLoading && <Loading />}
      {/* Une clé par travail : un nouveau lancement repart de sa propre proposition, pas des corrections du précédent. */}
      {travail.data && <ResultatInitialisation key={travail.data.id} t={travail.data} />}
    </div>
  );
}

const STATUT_LANCEMENT: Record<AdminInitialisationResumeDTO["statut"], { label: string; className: string }> = {
  EN_COURS: { label: "En cours", className: "bg-warn-bg text-warn-ink" },
  TERMINE: { label: "Terminée", className: "bg-positive-bg text-positive" },
  ECHEC: { label: "Échec", className: "bg-danger-bg text-danger" },
};

function etatEcriture(l: AdminInitialisationResumeDTO): string {
  const ecritures = l.ecritures ?? (l.ecriture ? [l.ecriture] : []);
  if (ecritures.length === 0) return l.ecriturePossible ? "Prête à écrire" : "—";
  const cible = (e: AdminEcritureResumeDTO) => `magasin ${e.magasin}${e.nomBoutique ? ` → ${e.nomBoutique}` : ""}`;
  const etat = (e: AdminEcritureResumeDTO) =>
    e.interrompue ? "interrompue" : e.statut === "EN_COURS" ? "en cours" : e.statut === "TERMINE" ? "écrite" : "en échec";
  // Une tentative en échec suivie d'une autre ne se dit plus : seule la dernière compte.
  const parlantes = ecritures.filter((e, i) => e.statut !== "ECHEC" || e.interrompue || i === ecritures.length - 1);
  const texte = parlantes.map((e) => `${cible(e)} : ${etat(e)}`).join(" · ");
  return l.ecriturePossible ? `${texte} · magasin suivant prêt à écrire` : texte;
}

/**
 * Les lancements que la caisse garde une semaine, déploiements compris : on
 * reprend l'un d'eux là où on l'avait laissé, sans relancer la lecture.
 */
function LancementsPrecedents({
  lancements,
  courant,
  onReprendre,
}: {
  lancements: AdminInitialisationResumeDTO[];
  courant: string | null;
  onReprendre: (id: string) => void;
}) {
  return (
    <Card pad="p-0" className="overflow-x-auto">
      <div className="px-5 pt-4">
        <SectionTitle>Lancements précédents</SectionTitle>
        <div className="mt-1 text-[12px] text-muted-2">Gardés une semaine par la caisse, même après une mise à jour.</div>
      </div>
      <table className="mt-2 w-full border-collapse text-[13px]">
        <thead>
          <tr className="text-left text-[11.5px] uppercase text-muted-2">
            <th className="px-5 py-3 font-bold">Lancé</th>
            <th className="px-3 py-3 font-bold">Compte</th>
            <th className="px-3 py-3 font-bold">Magasins</th>
            <th className="px-3 py-3 font-bold">Lecture</th>
            <th className="px-3 py-3 font-bold">Écriture</th>
            <th className="px-5 py-3" />
          </tr>
        </thead>
        <tbody>
          {lancements.map((l) => (
            <tr key={l.id} className={cx("border-t border-line-row align-top", l.id === courant && "bg-page")}>
              <td className="px-5 py-3 whitespace-nowrap" title={new Date(l.creeLe).toLocaleString("fr-FR")}>
                {relative(l.creeLe)}
              </td>
              <td className="px-3 py-3 font-semibold">{l.compte}</td>
              <td className="px-3 py-3 text-[12px] text-muted">{l.magasins.join(", ") || "—"}</td>
              <td className="px-3 py-3">
                <Pill className={STATUT_LANCEMENT[l.statut].className}>{STATUT_LANCEMENT[l.statut].label}</Pill>
                {l.erreur && <div className="mt-1 max-w-[280px] text-[11.5px] text-danger">{l.erreur}</div>}
              </td>
              <td className={cx("px-3 py-3 text-[12px]", (l.ecritures ?? (l.ecriture ? [l.ecriture] : [])).some((e) => e.interrompue) ? "text-danger" : "text-muted")}>
                {etatEcriture(l)}
              </td>
              <td className="px-5 py-3 text-right">
                {l.id === courant ? (
                  <span className="text-[12px] text-muted-2">Affiché</span>
                ) : (
                  <Button variant="ghost" onClick={() => onReprendre(l.id)}>
                    Reprendre
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const LIBELLES_TOTAUX: Record<string, string> = {
  produitsRepris: "Produits repris",
  produitsArchives: "Produits non repris",
  lignesDeStock: "Lignes de stock",
  clientsRepris: "Clients repris",
  clients: "Clients lus",
  vendeursActifs: "Vendeurs actifs",
  tickets: "Tickets d'historique",
  moisEnEcart: "Mois en écart",
  requetesHiboutik: "Requêtes Hiboutik",
  dureeSecondes: "Durée (s)",
};

type Unite = "GRAMME" | "PIECE";

/** Ce que la personne corrige dans la grille, par-dessus la proposition de la lecture. */
interface Corrections {
  produits: Record<string, { repris?: boolean; unite?: Unite }>;
  clients: Record<string, boolean>;
  remises: Record<string, boolean>;
  vendeurs: Record<string, boolean>;
}

type Bareme = AdminBaremeDTO;

const CORRECTIONS_VIDES: Corrections = { produits: {}, clients: {}, remises: {}, vendeurs: {} };

/** Les fichiers dont les lignes se cochent, et comment lire leur proposition. */
const EDITABLES: Record<string, { cle: string; reprisPropose: (l: Record<string, string>) => boolean }> = {
  "produits.csv": { cle: "id Hiboutik", reprisPropose: (l) => l["repris"]?.startsWith("oui") ?? false },
  "clients.csv": { cle: "id Hiboutik", reprisPropose: (l) => l["repris"] === "oui" },
  "remises.csv": { cle: "id", reprisPropose: (l) => l["repris"] === "oui" },
  // Proposé : actif et rattaché à au moins un magasin par ses ventes. À
  // l'écriture, seuls ceux du magasin choisi sont repris, sauf case touchée ici.
  "vendeurs.csv": { cle: "id Hiboutik", reprisPropose: (l) => l["actif"] === "oui" && Boolean(l["boutique(s) proposée(s)"]) },
};

function uniteProposee(l: Record<string, string>): Unite {
  return l["unité"] === "gramme" ? "GRAMME" : "PIECE";
}

function ResultatInitialisation({ t }: { t: AdminInitialisationDTO }) {
  const [fichier, setFichier] = useState("produits.csv");
  // Ce qu'on avait corrigé sur ce lancement avant un rechargement ou une mise à jour.
  const [retouches] = useState(() => lireRetouches(t.id));
  const [corrections, setCorrections] = useState<Corrections>(retouches.corrections);
  const [baremes, setBaremes] = useState<Bareme[]>(() => retouches.baremes ?? t.rapport?.baremes ?? []);
  const r = t.rapport;
  // La lecture à blanc était en cours au premier affichage : ses barèmes
  // arrivent avec le rapport, et se posent alors — sans recharger la page.
  const [baremesLus, setBaremesLus] = useState(Boolean(t.rapport) || retouches.baremes !== null);
  useEffect(() => {
    if (r) garderRetouches(t.id, corrections, baremesLus ? baremes : null);
  }, [t.id, r, corrections, baremes, baremesLus]);
  useEffect(() => {
    if (!baremesLus && r) {
      setBaremes(r.baremes ?? []);
      setBaremesLus(true);
    }
  }, [baremesLus, r]);
  const courant = r?.fichiers.find((f) => f.nom === fichier) ?? r?.fichiers[0] ?? null;
  // La grille se fige quand plus rien n'est à écrire : entre deux magasins, elle vaut pour le suivant.
  const ecrite = t.magasinsAEcrire ? t.magasinsAEcrire.length === 0 && ecrituresDe(t).length > 0 : t.ecriture != null && t.ecriture.statut !== "ECHEC";

  const nbCorrections =
    Object.keys(corrections.produits).length +
    Object.keys(corrections.clients).length +
    Object.keys(corrections.remises).length +
    Object.keys(corrections.vendeurs).length;

  return (
    <div className="flex flex-col gap-[18px]">
      <Card className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionTitle className="flex-1">Compte « {t.compte} »</SectionTitle>
          {t.statut === "EN_COURS" && <Pill className="bg-warn-bg text-warn-ink">En cours</Pill>}
          {t.statut === "TERMINE" && <Pill className="bg-positive-bg text-positive">Terminée — rien n&apos;a été écrit</Pill>}
          {t.statut === "ECHEC" && <Pill className="bg-danger-bg text-danger">Échec</Pill>}
          <span className="text-[11.5px] text-muted-2">Lancée {relative(t.creeLe)}</span>
        </div>
        {t.erreur && <ErrorState error={new Error(t.erreur)} />}
        {(t.statut === "EN_COURS" || !r) && (
          <div className="max-h-[220px] overflow-auto rounded-card-sm bg-page px-4 py-3 font-mono text-[12px] leading-[1.6]">
            {t.journal.map((l, i) => (
              <div key={i}>· {l}</div>
            ))}
            {t.progression && <div className="text-accent">· {t.progression}</div>}
          </div>
        )}
      </Card>

      {r && (
        <>
          <div className="flex flex-wrap gap-4">
            <StatCard className="flex-[1_1_160px]" label="Anomalies bloquantes" value={num(r.anomalies.bloquantes)} />
            <StatCard className="flex-[1_1_160px]" label="À vérifier" value={num(r.anomalies.aVerifier)} />
            <StatCard className="flex-[1_1_160px]" label="Pour information" value={num(r.anomalies.info)} />
          </div>
          <Card className="flex flex-col gap-3">
            <SectionTitle>Ce que la reprise ferait (proposition)</SectionTitle>
            <div className="text-[12.5px] text-muted">Magasins lus : {r.magasins.map((m) => `${m.nom} (${m.id})`).join(", ")}</div>
            <div className="grid gap-x-6 gap-y-2 [grid-template-columns:repeat(auto-fill,minmax(180px,1fr))]">
              {Object.entries(r.totaux)
                .filter(([k]) => k in LIBELLES_TOTAUX)
                .map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-2 border-b border-line-row py-[6px] text-[13px]">
                    <span className="text-muted">{LIBELLES_TOTAUX[k]}</span>
                    <span className="tabular font-bold">{num(v)}</span>
                  </div>
                ))}
            </div>
            <details>
              <summary className="cursor-pointer text-[12.5px] font-semibold text-accent">Résumé complet</summary>
              <pre className="mt-2 overflow-auto rounded-card-sm bg-page px-4 py-3 text-[11.5px] leading-[1.55]">{r.resume}</pre>
            </details>
          </Card>

          <Card className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <SectionTitle className="flex-1">Documents de la reprise</SectionTitle>
              {nbCorrections > 0 && (
                <>
                  <Pill className="bg-accent-tint text-accent">{nbCorrections} correction{nbCorrections > 1 ? "s" : ""}</Pill>
                  {!ecrite && (
                    <Button variant="ghost" onClick={() => setCorrections(CORRECTIONS_VIDES)}>
                      Revenir à la proposition
                    </Button>
                  )}
                </>
              )}
              <Button variant="outline" onClick={() => r.fichiers.forEach((f, i) => setTimeout(() => telecharger(f, t.compte), i * 250))}>
                Tout télécharger
              </Button>
            </div>
            <div className="text-[12px] text-muted-2">
              Produits, clients, remises et vendeurs se cochent ou se décochent ici ; l&apos;unité d&apos;un produit se
              corrige dans sa ligne. L&apos;écriture reprend exactement ce qui est coché (les vendeurs : ceux du magasin
              écrit, sauf case touchée).
            </div>
            <div className="flex flex-wrap gap-[6px]">
              {r.fichiers.map((f) => (
                <button
                  key={f.nom}
                  type="button"
                  onClick={() => setFichier(f.nom)}
                  className={cx(
                    "rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold",
                    courant?.nom === f.nom ? "bg-accent text-white" : "bg-page text-[oklch(0.5_0.012_150)]"
                  )}
                >
                  {f.nom.replace(/\.csv$/, "")} · {num(f.lignes.length)}
                </button>
              ))}
            </div>
            {courant && (
              <TableFichier
                f={courant}
                compte={t.compte}
                corrections={corrections}
                setCorrections={ecrite ? null : setCorrections}
                baremes={baremes}
                setBaremes={ecrite ? null : setBaremes}
              />
            )}
          </Card>

          <BaremesEditeur baremes={baremes} setBaremes={ecrite ? null : setBaremes} />

          <EcritureCaisse t={t} corrections={corrections} baremes={baremes} />
        </>
      )}
    </div>
  );
}

// --- Barèmes -----------------------------------------------------------------

const euroDe = (centimes: number | null | undefined) => (centimes == null ? "" : (centimes / 100).toFixed(2).replace(".", ","));
const centimesDe = (texte: string): number | null => {
  const n = Number(texte.replace(",", ".").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};
const pourcentDe = (bp: number | null | undefined) => (bp == null ? "" : (bp / 100).toString().replace(".", ","));
const bpDe = (texte: string): number | null => {
  const n = Number(texte.replace(",", ".").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) && n > 0 && n < 100 ? Math.round(n * 100) : null;
};

type ModeBareme = "UNIT_PRICE" | "DISCOUNT" | "PACKET_PRICE";
type Application = "ALL_UNITS" | "PACKET";

const MODES: { key: ModeBareme; label: string; aide: string }[] = [
  { key: "UNIT_PRICE", label: "Prix à l'unité", aide: "Un prix au gramme (ou à la pièce) par palier, le même pour tous les produits du barème." },
  { key: "DISCOUNT", label: "Remise en %", aide: "Chaque produit calcule la remise sur son propre prix." },
  { key: "PACKET_PRICE", label: "Prix du palier entier", aide: "« 3 g = 39,99 € » : le prix de toute la quantité du palier." },
];
const APPLICATIONS: { key: Application; label: string; aide: string }[] = [
  { key: "ALL_UNITS", label: "Toute la quantité", aide: "Le seuil atteint, toute la quantité passe au prix du palier." },
  { key: "PACKET", label: "Par paquet", aide: "Le palier ne vaut que pour sa quantité ; le reste est au prix de base." },
];

const uniteDe = (b: Bareme): Unite => b.unite ?? "GRAMME";
const modeDe = (b: Bareme): ModeBareme => b.mode ?? "UNIT_PRICE";
const applicationDe = (b: Bareme): Application => b.application ?? "ALL_UNITS";

/** Un palier est-il complet pour le mode de son barème ? */
function palierValide(b: Bareme, p: Bareme["paliers"][number]): boolean {
  return modeDe(b) === "DISCOUNT" ? (p.remiseBp ?? 0) >= 1 && (p.remiseBp ?? 0) <= 9999 : (p.prixCentimes ?? 0) > 0;
}

/**
 * Changer le mode garde ce qui se convertit : un prix à l'unité devient le
 * prix du palier entier (× seuil) et inversement ; une remise se propose à
 * 5 % de plus par palier quand il n'y en avait pas.
 */
function changerMode(b: Bareme, mode: ModeBareme): Bareme {
  const avant = modeDe(b);
  return {
    ...b,
    mode,
    paliers: b.paliers.map((p, i) => {
      if (mode === "DISCOUNT") return { ...p, remiseBp: p.remiseBp ?? Math.min(9000, 500 * (i + 1)) };
      if (avant === "UNIT_PRICE" && mode === "PACKET_PRICE" && p.prixCentimes) return { ...p, prixCentimes: p.prixCentimes * p.seuil };
      if (avant === "PACKET_PRICE" && mode === "UNIT_PRICE" && p.prixCentimes) return { ...p, prixCentimes: Math.max(1, Math.round(p.prixCentimes / p.seuil)) };
      return { ...p, prixCentimes: p.prixCentimes ?? 1000 };
    }),
  };
}

function BaremesEditeur({ baremes, setBaremes }: { baremes: Bareme[]; setBaremes: ((b: Bareme[]) => void) | null }) {
  const modifier = (id: string, f: (b: Bareme) => Bareme) => setBaremes?.(baremes.map((b) => (b.id === id ? f(b) : b)));
  const ajouter = () =>
    setBaremes?.([
      ...baremes,
      {
        id: `nouveau-${Date.now()}`,
        nom: "Nouveau barème",
        unite: "GRAMME",
        mode: "UNIT_PRICE",
        application: "ALL_UNITS",
        paliers: [1, 3, 6, 12, 24].map((seuil, i) => ({ seuil, prixCentimes: 1000 - 50 * i })),
        produits: [],
      },
    ]);
  const choix = "rounded-[10px] border border-line bg-white px-2 py-[6px] text-[12.5px] focus:border-accent";
  const petit = "rounded-[10px] border border-line bg-white px-2 py-[6px] text-[13px] tabular focus:border-accent";
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SectionTitle className="flex-1">Barèmes</SectionTitle>
        {setBaremes && (
          <Button variant="outline" onClick={ajouter}>
            + Nouveau barème
          </Button>
        )}
      </div>
      <div className="text-[12px] text-muted-2">
        Proposition : un barème de vrac par prix au gramme, paliers 1 / 3 / 6 / 12 / 24 g à −0,50 €/g. Chaque barème a son
        unité (gramme ou pièce), sa saisie (prix à l&apos;unité, remise en %, prix du palier entier) et son application (toute
        la quantité ou par paquet), comme dans le catalogue de la caisse. Les produits s&apos;y rattachent dans la grille des
        produits (colonne « Barème »), selon leur unité. Les grilles de déclinaisons Hiboutik ne sont pas reprises.
      </div>
      {baremes.length === 0 && <Empty>Aucun barème proposé.</Empty>}
      {baremes.map((b) => {
        const mode = modeDe(b);
        const unite = uniteDe(b) === "GRAMME" ? "g" : "pièce(s)";
        const paquet = applicationDe(b) === "PACKET";
        return (
          <div key={b.id} className="rounded-card-sm border border-line px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className={cx(inputClass, "max-w-[280px] font-semibold")}
                value={b.nom}
                disabled={!setBaremes}
                onChange={(e) => modifier(b.id, (x) => ({ ...x, nom: e.target.value }))}
              />
              <span className="flex-1 text-[12px] text-muted-2">
                {b.produits.length} produit{b.produits.length > 1 ? "s" : ""}
              </span>
              {setBaremes && (
                <Button variant="ghost" onClick={() => setBaremes(baremes.filter((x) => x.id !== b.id))}>
                  Supprimer
                </Button>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px]">
              <label className="flex items-center gap-1">
                <span className="text-muted">Unité</span>
                <select
                  className={choix}
                  value={uniteDe(b)}
                  disabled={!setBaremes}
                  // Changer d'unité détache les produits : un barème au gramme ne couvre pas un produit à la pièce.
                  onChange={(e) => modifier(b.id, (x) => ({ ...x, unite: e.target.value as Unite, produits: [] }))}
                >
                  <option value="GRAMME">gramme</option>
                  <option value="PIECE">pièce</option>
                </select>
              </label>
              <label className="flex items-center gap-1">
                <span className="text-muted">Saisie</span>
                <select className={choix} value={mode} disabled={!setBaremes} onChange={(e) => modifier(b.id, (x) => changerMode(x, e.target.value as ModeBareme))}>
                  {MODES.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1">
                <span className="text-muted">Application</span>
                <select
                  className={choix}
                  value={applicationDe(b)}
                  disabled={!setBaremes}
                  onChange={(e) => modifier(b.id, (x) => ({ ...x, application: e.target.value as Application }))}
                >
                  {APPLICATIONS.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </label>
              <span className="text-[11.5px] text-muted-2">
                {MODES.find((m) => m.key === mode)!.aide} {APPLICATIONS.find((a) => a.key === applicationDe(b))!.aide}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-3">
              {b.paliers.map((p, i) => (
                <div key={`${b.id}-${i}-${mode}`} className="flex items-center gap-1 whitespace-nowrap rounded-card-sm bg-page px-2 py-1 text-[12.5px]">
                  <span className="text-muted">{paquet ? "lot de" : "dès"}</span>
                  <input
                    className={cx(petit, "w-[64px]")}
                    type="number"
                    min={1}
                    value={p.seuil}
                    disabled={!setBaremes}
                    onChange={(e) =>
                      modifier(b.id, (x) => ({ ...x, paliers: x.paliers.map((q, j) => (j === i ? { ...q, seuil: Math.max(1, Number(e.target.value) || 1) } : q)) }))
                    }
                  />
                  <span className="text-muted">{unite} :</span>
                  {mode === "DISCOUNT" ? (
                    <>
                      <span className="text-muted">−</span>
                      <input
                        className={cx(petit, "w-[60px] text-right")}
                        defaultValue={pourcentDe(p.remiseBp)}
                        disabled={!setBaremes}
                        onBlur={(e) => {
                          const v = bpDe(e.target.value);
                          modifier(b.id, (x) => ({ ...x, paliers: x.paliers.map((q, j) => (j === i ? { ...q, remiseBp: v } : q)) }));
                        }}
                      />
                      <span className="text-muted">%</span>
                    </>
                  ) : (
                    <>
                      <input
                        className={cx(petit, "w-[76px] text-right")}
                        defaultValue={euroDe(p.prixCentimes)}
                        disabled={!setBaremes}
                        onBlur={(e) => {
                          const c = centimesDe(e.target.value);
                          modifier(b.id, (x) => ({ ...x, paliers: x.paliers.map((q, j) => (j === i ? { ...q, prixCentimes: c } : q)) }));
                        }}
                      />
                      <span className="text-muted">{mode === "PACKET_PRICE" ? "€ le palier" : `€/${uniteDe(b) === "GRAMME" ? "g" : "pièce"}`}</span>
                    </>
                  )}
                  {setBaremes && b.paliers.length > 1 && (
                    <button
                      type="button"
                      className="text-[14px] text-muted-2"
                      aria-label="Retirer ce palier"
                      onClick={() => modifier(b.id, (x) => ({ ...x, paliers: x.paliers.filter((_, j) => j !== i) }))}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {setBaremes && b.paliers.length < 6 && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    modifier(b.id, (x) => {
                      const dernier = x.paliers[x.paliers.length - 1];
                      const seuil = (dernier?.seuil ?? 0) * 2 || 1;
                      return {
                        ...x,
                        paliers: [
                          ...x.paliers,
                          modeDe(x) === "DISCOUNT"
                            ? { seuil, remiseBp: Math.min(9000, (dernier?.remiseBp ?? 0) + 500) }
                            : { seuil, prixCentimes: Math.max(10, (dernier?.prixCentimes ?? 1000) - 50) },
                        ],
                      };
                    })
                  }
                >
                  + palier
                </Button>
              )}
            </div>
            {b.paliers.some((p, i) => i > 0 && p.seuil <= b.paliers[i - 1]!.seuil) && (
              <div className="mt-2 text-[12px] text-danger">Les seuils doivent être strictement croissants.</div>
            )}
            {b.paliers.some((p) => !palierValide(b, p)) && (
              <div className="mt-2 text-[12px] text-danger">
                {mode === "DISCOUNT" ? "Chaque palier a une remise entre 0,01 et 99,99 %." : "Chaque palier a un prix."}
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}

// --- Écriture de la boutique dans la caisse ------------------------------------

const ETAPE_CLASSES: Record<"FAIT" | "ECHEC" | "SAUTE", string> = {
  FAIT: "bg-positive-bg text-positive",
  ECHEC: "bg-danger-bg text-danger",
  SAUTE: "bg-page text-muted",
};
const ETAPE_LIBELLES: Record<"FAIT" | "ECHEC" | "SAUTE", string> = { FAIT: "Fait", ECHEC: "Échec", SAUTE: "Sauté" };

const euros = (centimes: number) =>
  (centimes / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

/** Chiffres clés, PIN et code du poste : ce que le PDF remet au gérant. */
function Recapitulatif({ id, magasin, recap }: { id: string; magasin: number; recap: AdminRecapitulatifDTO }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const c = recap.chiffres;
  // Compté sur la liste affichée, comme dans le PDF : le chiffre et le tableau ne se contredisent pas.
  const vendeurs = recap.utilisateurs.filter((u) => u.role === "Vendeur").length;
  const telechargerPdf = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(`/api/v1/platform/administration/initialisations/${encodeURIComponent(id)}/recapitulatif.pdf?magasin=${magasin}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `Erreur ${res.status}`);
      }
      const nom = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "recapitulatif.pdf";
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = nom;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-3 rounded-card-sm border border-line-row px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <SectionTitle className="flex-1">Récapitulatif</SectionTitle>
        <Button onClick={() => void telechargerPdf()} disabled={busy}>
          {busy ? "Préparation…" : "Télécharger le récapitulatif (PDF)"}
        </Button>
      </div>
      {err != null && <ErrorState error={err} />}
      <div className="grid gap-x-6 gap-y-2 [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]">
        {[
          ["Clients", `${num(c.clients)}${c.clientsReseau ? ` (dont ${num(c.clientsReseau)} réseau)` : ""}`],
          ["Produits actifs", `${num(c.produitsActifs)} / ${num(c.produits)}`],
          ["Références en stock", num(c.referencesEnStock)],
          ["Stock à l'achat (HT)", euros(c.stockAchatHt)],
          ["Stock à la vente (TTC)", euros(c.stockVenteTtc)],
          ["Accès à la caisse", `${num(recap.utilisateurs.length)} (dont ${num(vendeurs)} vendeur${vendeurs > 1 ? "s" : ""})`],
        ].map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-2 border-b border-line-row py-[6px] text-[13px]">
            <span className="text-muted">{k}</span>
            <span className="tabular font-bold">{v}</span>
          </div>
        ))}
      </div>
      {recap.acces && (
        <div className="flex flex-col gap-1 text-[13px]">
          {recap.acces.backOffice && (
            <div>
              Back-office : <mark className="rounded bg-[#fff176] px-1 font-semibold">{recap.acces.backOffice}</mark>
            </div>
          )}
          <div>
            Identifiant : <mark className="rounded bg-[#fff176] px-1 font-semibold">{recap.acces.identifiant}</mark>
          </div>
          {recap.acces.lienMotDePasse && (
            <div className="break-all">
              Mot de passe : <mark className="rounded bg-[#fff176] px-1 font-semibold">{recap.acces.lienMotDePasse}</mark>
            </div>
          )}
        </div>
      )}
      {recap.caisse ? (
        <div className="text-[13px]">
          Caisse <b>{recap.caisse.nom}</b>, poste <b>{recap.caisse.poste}</b> — code{" "}
          <mark className="rounded bg-[#fff176] px-2 py-[2px] font-mono font-bold tracking-wider">{recap.caisse.code}</mark>, à saisir une fois
          sur l&apos;ordinateur du comptoir
          {recap.caisse.expireLe ? ` avant le ${new Date(recap.caisse.expireLe).toLocaleString("fr-FR")}` : ", sans date limite"}.
        </div>
      ) : (
        <div className="text-[13px] text-danger">La caisse n&apos;a pas pu être créée : à faire dans Paramètres › Caisses.</div>
      )}
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-[11px] uppercase text-muted-2">
            <th className="py-1">Nom</th>
            <th className="py-1">Rôle</th>
            <th className="py-1">PIN</th>
            <th className="py-1">Connexion back-office</th>
          </tr>
        </thead>
        <tbody>
          {recap.utilisateurs.map((u, i) => (
            <tr key={i} className="border-t border-line-row">
              <td className="py-[6px]">{u.nom}</td>
              <td className="py-[6px]">{u.role}</td>
              <td className="py-[6px]">
                {u.pinHabituel ? "son code habituel" : u.pin ? <mark className="rounded bg-[#fff176] px-1 font-mono font-bold">{u.pin}</mark> : "—"}
              </td>
              <td className="py-[6px] text-muted">{[u.email ?? (u.remarque ? null : "aucune (caisse seulement)"), u.remarque].filter(Boolean).join(" — ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[11.5px] text-muted-2">
        Les PIN et le code ne sont lisibles qu&apos;ici, tant que la caisse garde ce travail (quelques heures) : téléchargez le
        PDF et remettez-le en main propre.
      </div>
    </div>
  );
}

/** Les écritures d'une lecture ; une caisse d'avant l'enchaînement n'en rend qu'une. */
function ecrituresDe(t: AdminInitialisationDTO): AdminEcritureDTO[] {
  return t.ecritures ?? (t.ecriture ? [t.ecriture] : []);
}

/**
 * L'écriture dans la caisse, magasin après magasin : une lecture à blanc sert
 * tous les magasins du compte. Chaque boutique écrite garde son suivi et son
 * récapitulatif ; le formulaire du magasin suivant vient dessous, tant qu'il en
 * reste un.
 */
function EcritureCaisse({ t, corrections, baremes }: { t: AdminInitialisationDTO; corrections: Corrections; baremes: Bareme[] }) {
  const r = t.rapport!;
  const ecritures = ecrituresDe(t);
  // Une caisse d'avant l'enchaînement ne dit pas ce qui reste : une seule écriture par lecture.
  const restants = t.magasinsAEcrire ?? (t.ecriturePossible ? r.magasins.map((m) => m.id) : []);
  // Une tentative en échec ne compte pas comme une boutique : elle se dit dans le formulaire, qui la relance.
  const suivies = ecritures.filter((e) => e.statut !== "ECHEC" || e.interrompue);
  const derniere = ecritures.at(-1);
  const echec = derniere?.statut === "ECHEC" && !derniere.interrompue ? derniere : null;
  const precedente = [...ecritures].reverse().find((e) => e.statut === "TERMINE" && e.resultat) ?? null;

  return (
    <>
      {suivies.map((e, i) => (
        <EcritureSuivie key={`${e.demande.magasin}-${i}`} t={t} e={e} />
      ))}
      {restants.length > 0 ? (
        <FormulaireEcriture
          // Un nouveau formulaire à chaque boutique écrite : il repart de la précédente.
          key={`${suivies.length}-${restants.join(",")}`}
          t={t}
          corrections={corrections}
          baremes={baremes}
          restants={restants}
          precedente={precedente}
          echec={echec}
          suivant={suivies.length > 0}
        />
      ) : (
        suivies.length === 0 && (
          <Card>
            <SectionTitle>Écrire la boutique dans la caisse</SectionTitle>
            <div className="mt-1 text-[12.5px] text-muted">L&apos;écriture n&apos;est pas proposée par cette caisse.</div>
          </Card>
        )
      )}
    </>
  );
}

/** Le suivi d'une boutique écrite (ou en cours d'écriture, ou coupée) depuis cette lecture. */
function EcritureSuivie({ t, e }: { t: AdminInitialisationDTO; e: AdminEcritureDTO }) {
  const res = e.resultat;
  const magasin = t.rapport?.magasins.find((m) => m.id === e.demande.magasin);
  const nom = res?.nomBoutique ?? e.demande.nomBoutique ?? magasin?.nom ?? `magasin ${e.demande.magasin}`;
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SectionTitle className="flex-1">
          Écriture dans la caisse — {nom}
          <span className="ml-2 text-[12px] font-normal text-muted-2">magasin Hiboutik {e.demande.magasin}</span>
        </SectionTitle>
        {e.statut === "EN_COURS" && <Pill className="bg-warn-bg text-warn-ink">En cours</Pill>}
        {e.statut === "TERMINE" && <Pill className="bg-positive-bg text-positive">Boutique créée</Pill>}
        {e.interrompue && <Pill className="bg-danger-bg text-danger">Interrompue</Pill>}
        {e.statut === "TERMINE" && e.suite === "EN_COURS" && (
          <Pill className="bg-warn-bg text-warn-ink">Soldes et photos en cours</Pill>
        )}
      </div>
      {e.interrompue && <ErrorState error={new Error(e.erreur ?? "Écriture interrompue par un redémarrage de la caisse.")} />}
      {e.statut === "EN_COURS" && (
        <div className="max-h-[220px] overflow-auto rounded-card-sm bg-page px-4 py-3 font-mono text-[12px] leading-[1.6]">
          {e.journal.length === 0 ? <div>· démarrage…</div> : e.journal.map((l, i) => <div key={i}>· {l}</div>)}
        </div>
      )}
      {res && (
        <>
          <div className="text-[13px]">
            <b>{res.nomBoutique}</b> existe dans la caisse Easy Shop, branchée sur le compte « {t.compte} » en écritures
            refusées : la caisse lit Hiboutik, elle n&apos;y écrit rien tant qu&apos;on ne l&apos;a pas décidé dans ses
            Paramètres › Intégrations.
          </div>
          {e.suite === "EN_COURS" && (
            <div className="rounded-card-sm bg-warn-bg px-4 py-3 text-[12.5px] leading-[1.55] text-warn-ink">
              La boutique est utilisable. La caisse reprend encore, en tâche de fond, les soldes et statuts de fidélité
              puis les photos du catalogue : leur bilan s&apos;ajoute aux étapes ci-dessous en finissant. Ne redéployez
              pas la caisse d&apos;ici là : des soldes interrompus seraient à reprendre.
            </div>
          )}
          <div className="flex flex-col gap-[6px]">
            {res.etapes.map((x, i) => (
              <div key={i} className="flex items-start gap-2 border-b border-line-row py-[6px] text-[12.5px]">
                <Pill className={ETAPE_CLASSES[x.statut]}>{ETAPE_LIBELLES[x.statut]}</Pill>
                <span className="w-[170px] flex-shrink-0 font-semibold">{x.etape}</span>
                <span className="text-muted">{x.detail}</span>
              </div>
            ))}
          </div>
          <div className="rounded-card-sm bg-page px-4 py-3 text-[12.5px] leading-[1.55]">
            Propriétaire : <b>{res.proprietaire.email}</b>
            {res.proprietaire.cree ? " (compte créé)" : " (compte existant, ses accès s'ajoutent)"}.
            {res.proprietaire.lienMotDePasse && (
              <>
                <br />
                Lien pour choisir son mot de passe, valable une heure, à lui transmettre :
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <code className="max-w-full break-all rounded bg-white px-2 py-1 text-[11.5px]">{res.proprietaire.lienMotDePasse}</code>
                  <Button variant="outline" onClick={() => void navigator.clipboard?.writeText(res.proprietaire.lienMotDePasse!)}>
                    Copier le lien
                  </Button>
                </div>
              </>
            )}
          </div>
          {res.recapitulatif && <Recapitulatif id={t.id} magasin={e.demande.magasin} recap={res.recapitulatif} />}
          <div className="text-[12px] text-muted-2">
            Pour voir ses chiffres ici : onglet « Boutiques de la caisse », puis « Lier au tableau de bord ».
          </div>
        </>
      )}
    </Card>
  );
}

function FormulaireEcriture({
  t,
  corrections,
  baremes,
  restants,
  precedente,
  echec,
  suivant,
}: {
  t: AdminInitialisationDTO;
  corrections: Corrections;
  baremes: Bareme[];
  /** Les magasins de la lecture qui restent à écrire. */
  restants: number[];
  /** La dernière boutique écrite depuis cette lecture : le formulaire en reprend le réseau et le propriétaire. */
  precedente: AdminEcritureDTO | null;
  /** La dernière tentative, si elle a échoué. */
  echec: AdminEcritureDTO | null;
  /** Une boutique a déjà été écrite depuis cette lecture. */
  suivant: boolean;
}) {
  const qc = useQueryClient();
  const r = t.rapport!;
  const reseaux = useAdminEasyshopReseaux(t.ecriturePossible || restants.length > 0);
  const premier = r.magasins.find((m) => m.id === (echec && restants.includes(echec.demande.magasin) ? echec.demande.magasin : restants[0]));
  const [magasin, setMagasin] = useState<number>(premier?.id ?? 0);
  // Le magasin suivant rejoint le réseau du précédent, avec le même propriétaire et les mêmes options.
  const [reseauId, setReseauId] = useState<string>(precedente?.resultat?.networkId ?? "nouveau");
  const [nouveauReseau, setNouveauReseau] = useState("");
  const [email, setEmail] = useState(precedente?.demande.proprietaire.email ?? t.utilisateurApi ?? "");
  const [nomProprietaire, setNomProprietaire] = useState(precedente?.demande.proprietaire.nom ?? "");
  const [nomBoutique, setNomBoutique] = useState(premier?.nom ?? "");
  const [anomaliesLues, setAnomaliesLues] = useState(suivant);
  const [clientsSansBoutique, setClientsSansBoutique] = useState(false);
  const [reprendreSoldes, setReprendreSoldes] = useState(precedente?.demande.reprendreSoldes ?? true);
  const [clientsReseau, setClientsReseau] = useState(precedente?.demande.clientsReseau ?? false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  // Un réseau créé par l'écriture précédente n'est pas encore dans la liste lue avant elle.
  useEffect(() => {
    if (precedente?.resultat) void qc.invalidateQueries({ queryKey: ["admin-easyshop-reseaux"] });
  }, [qc, precedente]);

  const choisirMagasin = (id: number) => {
    setMagasin(id);
    setNomBoutique(r.magasins.find((m) => m.id === id)?.nom ?? "");
  };

  const ecrire = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/platform/administration/initialisations/${t.id}/ecrire`, {
        magasin,
        reseau: reseauId === "nouveau" ? { nouveau: nouveauReseau.trim() } : { id: reseauId },
        proprietaire: { email: email.trim(), nom: nomProprietaire.trim() || null },
        nomBoutique: nomBoutique.trim() || null,
        corrections: {
          ...corrections,
          ...(r.magasins.length > 1 ? { clientsSansBoutique } : {}),
        },
        baremes: baremes.filter((b) => b.produits.length > 0),
        reprendreSoldes,
        clientsReseau,
      });
      await qc.invalidateQueries({ queryKey: ["admin-initialisation", t.id] });
      await qc.invalidateQueries({ queryKey: ["admin-initialisations"] });
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  };

  const baremesValides = baremes.every(
    (b) => b.nom.trim() && b.paliers.every((p, i) => palierValide(b, p) && (i === 0 || p.seuil > b.paliers[i - 1]!.seuil))
  );
  const pret =
    baremesValides &&
    t.ecriturePossible &&
    restants.includes(magasin) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    (reseauId !== "nouveau" || nouveauReseau.trim().length >= 2) &&
    (r.anomalies.bloquantes === 0 || anomaliesLues);
  const reseauConnu = reseauId === "nouveau" || (reseaux.data ?? []).some((n) => n.id === reseauId);

  return (
    <Card className="flex flex-col gap-4">
      <div>
        <SectionTitle>{suivant ? "Écrire le magasin suivant" : "Écrire la boutique dans la caisse"}</SectionTitle>
        <div className="mt-1 text-[12.5px] leading-[1.5] text-muted">
          {suivant ? (
            <>
              La même lecture sert tous les magasins du compte : inutile de la relancer. Le réseau, le propriétaire et
              les options de la boutique précédente sont repris ci-dessous ; les clients déjà repris en fiches réseau
              sont retrouvés, pas recréés.
            </>
          ) : (
            <>
              Crée dans la caisse Easy Shop la boutique du magasin choisi, à partir de cette lecture : identité légale,
              branchement sur le compte Hiboutik (écritures refusées), catalogue, barèmes, stock de départ, clients
              cochés et, si vous le voulez, leurs soldes et statuts, vendeurs, puis reprise de l&apos;historique des
              ventes. Rien n&apos;est envoyé aux clients : le lien pour compléter une fiche ne part que depuis la caisse.
              {r.magasins.length > 1 && " Les autres magasins du compte s'écrivent ensuite depuis la même lecture."}
            </>
          )}
        </div>
      </div>
      {echec && <ErrorState error={new Error(`La précédente tentative a échoué : ${echec.erreur ?? "erreur inconnue"}`)} />}
      {t.attente && (
        <div className="rounded-card-sm bg-warn-bg px-4 py-3 text-[12.5px] leading-[1.55] text-warn-ink">{t.attente}</div>
      )}
      <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
        <label>
          <FieldLabel>Magasin Hiboutik</FieldLabel>
          <select className={inputClass} value={magasin} onChange={(x) => choisirMagasin(Number(x.target.value))}>
            {r.magasins.map((m) => (
              <option key={m.id} value={m.id} disabled={!restants.includes(m.id)}>
                {m.nom} ({m.id}){restants.includes(m.id) ? "" : " — déjà écrit"}
              </option>
            ))}
          </select>
        </label>
        <label>
          <FieldLabel>Nom de la boutique</FieldLabel>
          <input className={inputClass} value={nomBoutique} onChange={(x) => setNomBoutique(x.target.value)} />
        </label>
        <label>
          <FieldLabel>Réseau dans la caisse</FieldLabel>
          <select className={inputClass} value={reseauId} onChange={(x) => setReseauId(x.target.value)}>
            <option value="nouveau">Nouveau réseau…</option>
            {!reseauConnu && <option value={reseauId}>Réseau de la boutique précédente</option>}
            {(reseaux.data ?? []).map((n) => (
              <option key={n.id} value={n.id}>
                {n.name} ({n.boutiques} boutique{n.boutiques > 1 ? "s" : ""})
              </option>
            ))}
          </select>
        </label>
        {reseauId === "nouveau" && (
          <label>
            <FieldLabel>Nom du nouveau réseau</FieldLabel>
            <input className={inputClass} value={nouveauReseau} onChange={(x) => setNouveauReseau(x.target.value)} placeholder="Ex. Purple Store Saint-Nazaire" />
          </label>
        )}
        <label>
          <FieldLabel>E-mail du propriétaire</FieldLabel>
          <input className={inputClass} type="email" value={email} onChange={(x) => setEmail(x.target.value)} />
          <div className="mt-[6px] text-[11.5px] text-muted-2">
            {suivant ? "Repris de la boutique précédente." : "Proposé : l'identifiant API du compte."} Un compte existant est réutilisé.
          </div>
        </label>
        <label>
          <FieldLabel>Nom du propriétaire</FieldLabel>
          <input className={inputClass} value={nomProprietaire} onChange={(x) => setNomProprietaire(x.target.value)} placeholder="Facultatif" />
        </label>
        <label className="col-span-full flex items-start gap-2 text-[13px]">
          <input type="checkbox" className="mt-[3px]" checked={reprendreSoldes} onChange={(x) => setReprendreSoldes(x.target.checked)} />
          <span>
            Reprendre les soldes et statuts de fidélité des clients repris
            <span className="block text-[11.5px] text-muted-2">
              Décoché : les clients sont créés sans points ni statut (utile en recette). Les clients repris sont ceux cochés
              dans la grille. Aucun e-mail ni SMS ne leur est envoyé.
            </span>
          </span>
        </label>
        <label className="col-span-full flex items-start gap-2 text-[13px]">
          <input type="checkbox" className="mt-[3px]" checked={clientsReseau} onChange={(x) => setClientsReseau(x.target.checked)} />
          <span>
            Créer les clients en fiches réseau
            <span className="block text-[11.5px] text-muted-2">
              Vus de toutes les boutiques du réseau, avec un seul compte de fidélité. Tous les clients cochés dans la grille sont
              repris, quelle que soit leur boutique ; en écrivant un second magasin dans le même réseau, ceux déjà repris sont
              retrouvés au lieu d&apos;être recréés — par leur fiche Hiboutik quand le magasin est sur le même compte, même sans
              téléphone ni e-mail, sinon par leur téléphone ou leur e-mail. Décoché : chaque fiche reste à sa boutique.
            </span>
          </span>
        </label>
        {r.magasins.length > 1 && !clientsReseau && (
          <label className="col-span-full flex items-start gap-2 text-[13px]">
            <input type="checkbox" className="mt-[3px]" checked={clientsSansBoutique} onChange={(x) => setClientsSansBoutique(x.target.checked)} />
            <span>
              Reprendre aussi dans cette boutique les clients sans boutique de rattachement
              <span className="block text-[11.5px] text-muted-2">
                Ceux qui n&apos;ont aucun achat dans l&apos;historique lu : sinon chacun irait dans toutes les boutiques du compte.
              </span>
            </span>
          </label>
        )}
        {r.anomalies.bloquantes > 0 && (
          <label className="col-span-full flex items-start gap-2 text-[13px]">
            <input type="checkbox" className="mt-[3px]" checked={anomaliesLues} onChange={(x) => setAnomaliesLues(x.target.checked)} />
            <span>
              J&apos;ai relu les {r.anomalies.bloquantes} anomalie{r.anomalies.bloquantes > 1 ? "s" : ""} bloquante
              {r.anomalies.bloquantes > 1 ? "s" : ""}
              <span className="block text-[11.5px] text-muted-2">
                Ce qui manque (SIRET, TVA…) reste à compléter dans la caisse, Paramètres › Informations légales.
              </span>
            </span>
          </label>
        )}
      </div>
      {err != null && <ErrorState error={err} />}
      <div>
        <Button variant="danger" onClick={() => void ecrire()} disabled={!pret || busy}>
          {busy ? "Lancement…" : suivant ? "Écrire ce magasin dans la caisse" : "Écrire la boutique dans la caisse"}
        </Button>
      </div>
    </Card>
  );
}

function telecharger(f: AdminInitialisationFileDTO, compte: string) {
  // Le CSV de la caisse porte déjà son BOM UTF-8 et le séparateur « ; » d'Excel FR.
  const url = URL.createObjectURL(new Blob([f.csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `initialisation-${compte}-${f.nom}`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

const LIGNES_AFFICHEES = 300;

function TableFichier({
  f,
  compte,
  corrections,
  setCorrections,
  baremes,
  setBaremes,
}: {
  f: AdminInitialisationFileDTO;
  compte: string;
  corrections: Corrections;
  setCorrections: ((c: Corrections) => void) | null;
  baremes: Bareme[];
  setBaremes: ((b: Bareme[]) => void) | null;
}) {
  const [q, setQ] = useState("");
  const lignes = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? f.lignes.filter((l) => Object.values(l).some((v) => v.toLowerCase().includes(needle))) : f.lignes;
  }, [f, q]);
  const edition = EDITABLES[f.nom];
  const produits = f.nom === "produits.csv";
  const tableDe = (c: Corrections): Record<string, boolean> =>
    f.nom === "clients.csv" ? c.clients : f.nom === "vendeurs.csv" ? c.vendeurs : c.remises;

  const repris = (l: Record<string, string>): boolean => {
    if (!edition) return false;
    const id = l[edition.cle] ?? "";
    if (produits) return corrections.produits[id]?.repris ?? edition.reprisPropose(l);
    const table = tableDe(corrections);
    return id in table ? table[id]! : edition.reprisPropose(l);
  };
  const unite = (l: Record<string, string>): Unite => corrections.produits[l["id Hiboutik"] ?? ""]?.unite ?? uniteProposee(l);
  const baremeDe = (id: number) => baremes.find((b) => b.produits.includes(id))?.id ?? "";

  function poser(lignesVisees: Record<string, string>[], valeur: boolean) {
    if (!edition || !setCorrections) return;
    const suivant: Corrections = {
      produits: { ...corrections.produits },
      clients: { ...corrections.clients },
      remises: { ...corrections.remises },
      vendeurs: { ...corrections.vendeurs },
    };
    for (const l of lignesVisees) {
      const id = l[edition.cle] ?? "";
      const propose = edition.reprisPropose(l);
      if (produits) {
        const actuel = { ...suivant.produits[id] };
        if (valeur === propose) delete actuel.repris;
        else actuel.repris = valeur;
        if (Object.keys(actuel).length) suivant.produits[id] = actuel;
        else delete suivant.produits[id];
      } else {
        const table = tableDe(suivant);
        if (valeur === propose) delete table[id];
        else table[id] = valeur;
      }
    }
    setCorrections(suivant);
  }

  function changerUnite(l: Record<string, string>, u: Unite) {
    if (!setCorrections) return;
    const id = l["id Hiboutik"] ?? "";
    const actuel = { ...corrections.produits[id] };
    if (u === uniteProposee(l)) delete actuel.unite;
    else actuel.unite = u;
    const produitsCorr = { ...corrections.produits };
    if (Object.keys(actuel).length) produitsCorr[id] = actuel;
    else delete produitsCorr[id];
    setCorrections({ ...corrections, produits: produitsCorr });
    // Un produit qui change d'unité quitte les barèmes de l'autre unité.
    if (setBaremes) setBaremes(baremes.map((b) => (uniteDe(b) === u ? b : { ...b, produits: b.produits.filter((x) => x !== Number(id)) })));
  }

  function changerBareme(idProduit: number, idBareme: string) {
    if (!setBaremes) return;
    setBaremes(
      baremes.map((b) => ({
        ...b,
        produits: b.id === idBareme ? [...b.produits.filter((x) => x !== idProduit), idProduit] : b.produits.filter((x) => x !== idProduit),
      }))
    );
  }

  const affichees = lignes.slice(0, LIGNES_AFFICHEES);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <input className={cx(inputClass, "max-w-[320px]")} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer les lignes…" />
        <span className="flex-1 text-[12px] text-muted-2">
          {num(lignes.length)} ligne{lignes.length > 1 ? "s" : ""}
          {edition ? ` · ${num(lignes.filter(repris).length)} reprise${lignes.filter(repris).length > 1 ? "s" : ""}` : ""}
          {lignes.length > LIGNES_AFFICHEES ? ` — ${LIGNES_AFFICHEES} premières affichées` : ""}
        </span>
        {edition && setCorrections && (
          <>
            <Button variant="ghost" onClick={() => poser(lignes, true)}>
              Cocher {q ? "le filtre" : "tout"}
            </Button>
            <Button variant="ghost" onClick={() => poser(lignes, false)}>
              Décocher {q ? "le filtre" : "tout"}
            </Button>
          </>
        )}
        <Button variant="outline" onClick={() => telecharger(f, compte)}>
          Télécharger {f.nom}
        </Button>
      </div>
      {f.lignes.length === 0 ? (
        <Empty>Fichier vide.</Empty>
      ) : (
        <div className="max-h-[520px] overflow-auto rounded-card-sm border border-line">
          <table className="w-full border-collapse text-[12px]">
            <thead className="sticky top-0 z-[1] bg-page">
              <tr>
                {edition && <th className="border-b border-line px-3 py-2 text-left font-bold text-muted">Reprendre</th>}
                {produits && <th className="border-b border-line px-3 py-2 text-left font-bold text-muted">Unité</th>}
                {produits && <th className="border-b border-line px-3 py-2 text-left font-bold text-muted">Barème</th>}
                {f.entetes
                  .filter((h) => !(edition && (h === "repris" || (produits && h === "unité"))))
                  .map((h) => (
                    <th key={h} className="whitespace-nowrap border-b border-line px-3 py-2 text-left font-bold text-muted">
                      {h}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {affichees.map((l, i) => {
                const pris = repris(l);
                const id = Number(l["id Hiboutik"]);
                return (
                  <tr
                    key={i}
                    className={cx(
                      "border-b border-line-row",
                      f.nom === "anomalies.csv" && graviteClass(l["gravité"]),
                      edition && !pris && "text-muted-2"
                    )}
                  >
                    {edition && (
                      <td className="px-3 py-[6px] align-top">
                        <input
                          type="checkbox"
                          checked={pris}
                          disabled={!setCorrections}
                          aria-label="Reprendre cette ligne"
                          onChange={(e) => poser([l], e.target.checked)}
                        />
                      </td>
                    )}
                    {produits && (
                      <td className="px-3 py-[6px] align-top">
                        <select
                          className="rounded border border-line bg-white px-1 py-[2px] text-[12px]"
                          value={unite(l)}
                          disabled={!setCorrections}
                          onChange={(e) => changerUnite(l, e.target.value as Unite)}
                        >
                          <option value="PIECE">pièce</option>
                          <option value="GRAMME">gramme</option>
                        </select>
                      </td>
                    )}
                    {produits && (
                      <td className="px-3 py-[6px] align-top">
                        {baremes.some((b) => uniteDe(b) === unite(l)) ? (
                          <select
                            className="max-w-[160px] rounded border border-line bg-white px-1 py-[2px] text-[12px]"
                            value={baremeDe(id)}
                            disabled={!setBaremes}
                            onChange={(e) => changerBareme(id, e.target.value)}
                          >
                            <option value="">aucun</option>
                            {baremes
                              .filter((b) => uniteDe(b) === unite(l))
                              .map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.nom}
                                </option>
                              ))}
                          </select>
                        ) : (
                          <span className="text-muted-2">—</span>
                        )}
                      </td>
                    )}
                    {f.entetes
                      .filter((h) => !(edition && (h === "repris" || (produits && h === "unité"))))
                      .map((h) => (
                        <td key={h} className="max-w-[360px] px-3 py-[6px] align-top">
                          {l[h]}
                        </td>
                      ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function graviteClass(g: string | undefined): string | false {
  if (g === "bloquant") return "bg-danger-bg";
  if (g === "à vérifier") return "bg-warn-bg";
  return false;
}

// --- Boutiques de la caisse ---------------------------------------------------

const MOTEUR: Record<AdminEasyshopBoutiqueDTO["engine"], { label: string; className: string }> = {
  LOCAL: { label: "Caisse Easy Shop", className: "bg-positive-bg text-positive" },
  EXTERNAL: { label: "Encore sur Hiboutik", className: "bg-franchise-bg text-franchise" },
  NONE: { label: "Sans caisse active", className: "bg-page text-muted" },
};

function BoutiquesCaisse({ disponible }: { disponible: boolean }) {
  const q = useAdminEasyshopBoutiques(disponible);
  const qc = useQueryClient();
  const [lier, setLier] = useState<AdminEasyshopBoutiqueDTO | null>(null);
  const [action, setAction] = useState<{ id: string; label: string } | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["admin-easyshop-boutiques"] });
    void qc.invalidateQueries({ queryKey: ["platform-boutiques"] });
    void qc.invalidateQueries({ queryKey: ["admin-boutiques"] });
    void qc.invalidateQueries({ queryKey: ["admin-status"] });
  };

  const reprendre = async (b: AdminEasyshopBoutiqueDTO) => {
    if (!b.linked) return;
    setAction({ id: b.tenantId, label: "Reprise…" });
    setErr(null);
    setInfo(null);
    try {
      const r = await api.post<{ ok: boolean; days: number; sales: number; error?: string }>(
        `/platform/administration/easyshop/links/${b.linked.boutiqueId}/sync`,
        { months: 13 }
      );
      if (!r.ok) throw new Error(r.error ?? "Reprise impossible");
      setInfo(`${b.linked.name} : ${num(r.days)} jours repris, ${num(r.sales)} ventes.`);
      refresh();
    } catch (e) {
      setErr(e);
    } finally {
      setAction(null);
    }
  };

  const delier = async (b: AdminEasyshopBoutiqueDTO) => {
    if (!b.linked) return;
    if (!window.confirm(`Délier « ${b.linked.name} » ? Elle repassera sur la synchro Hiboutik.`)) return;
    setAction({ id: b.tenantId, label: "…" });
    setErr(null);
    try {
      await api.del(`/platform/administration/easyshop/links/${b.linked.boutiqueId}`);
      refresh();
    } catch (e) {
      setErr(e);
    } finally {
      setAction(null);
    }
  };

  if (!disponible) return <Empty>Connectez d&apos;abord la caisse Easy Shop (voir l&apos;état en haut de page).</Empty>;

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="text-[12.5px] leading-[1.5] text-muted">
        Une boutique qui encaisse sur la caisse Easy Shop n&apos;a plus de chiffres chez Hiboutik : liez-la ici à une boutique
        du tableau de bord (existante ou nouvelle). Ses ventes, son panier moyen et son stock remontent alors à chaque
        synchronisation, et « Reprendre l&apos;historique » rapatrie les 13 derniers mois.
      </div>
      {err != null && <ErrorState error={err} />}
      {info && <div className="rounded-card-sm bg-positive-bg px-4 py-3 text-[13px] font-semibold text-positive">{info}</div>}
      {q.isLoading && <Loading />}
      {q.error && <ErrorState error={q.error} />}
      {q.data && q.data.length === 0 && <Empty>Aucune boutique dans la caisse.</Empty>}
      {q.data && q.data.length > 0 && (
        <Card pad="p-0" className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] uppercase text-muted-2">
                <th className="px-5 py-3 font-bold">Boutique de la caisse</th>
                <th className="px-3 py-3 font-bold">Encaisse sur</th>
                <th className="px-3 py-3 font-bold">Tableau de bord</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {q.data.map((b) => (
                <tr key={b.tenantId} className="border-t border-line-row align-top">
                  <td className="px-5 py-3">
                    <div className="font-semibold">{b.name}</div>
                    <div className="text-[11.5px] text-muted-2">
                      Réseau {b.network.name}
                      {b.externalPos?.account ? ` · ${b.externalPos.account}, magasin ${b.externalPos.storeId ?? 1}` : ""}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <Pill className={MOTEUR[b.engine].className}>{MOTEUR[b.engine].label}</Pill>
                  </td>
                  <td className="px-3 py-3">
                    {b.linked ? (
                      <>
                        <div className="font-semibold">
                          {b.linked.name} <span className="font-normal text-muted-2">· {b.linked.organization.name}</span>
                        </div>
                        <div className={cx("text-[11.5px]", b.linked.lastSyncError ? "text-danger" : "text-muted-2")}>
                          {b.linked.lastSyncError ?? (b.linked.lastSyncAt ? `Synchronisée ${relative(b.linked.lastSyncAt)}` : "Jamais synchronisée")}
                        </div>
                      </>
                    ) : b.suggestion ? (
                      <div className="text-[12px] text-muted">
                        Non liée — correspond à <b>{b.suggestion.name}</b> ({b.suggestion.organization.name})
                      </div>
                    ) : (
                      <div className="text-[12px] text-muted">Non liée</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      {action?.id === b.tenantId ? (
                        <span className="text-[12px] text-muted">{action.label}</span>
                      ) : b.linked ? (
                        <>
                          <Button variant="outline" onClick={() => void reprendre(b)}>
                            Reprendre l&apos;historique
                          </Button>
                          <Button variant="ghost" onClick={() => void delier(b)}>
                            Délier
                          </Button>
                        </>
                      ) : (
                        <Button onClick={() => setLier(b)}>Lier au tableau de bord</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <LierDrawer
        cible={lier}
        onClose={() => setLier(null)}
        onDone={(msg) => {
          setInfo(msg);
          refresh();
        }}
      />
    </div>
  );
}

function LierDrawer({
  cible,
  onClose,
  onDone,
}: {
  cible: AdminEasyshopBoutiqueDTO | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const boutiques = usePlatformBoutiques();
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [boutiqueId, setBoutiqueId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [type, setType] = useState<"INTEGRATED" | "FRANCHISE">("FRANCHISE");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  useEffect(() => {
    if (!cible) return;
    setMode(cible.suggestion ? "existing" : "new");
    setBoutiqueId(cible.suggestion?.boutiqueId ?? "");
    setName(cible.name);
    setCity(cible.name);
    setOrganizationId("");
    setErr(null);
  }, [cible]);

  const reseaux = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of boutiques.data ?? []) m.set(b.organization.id, b.organization.name);
    return [...m.entries()];
  }, [boutiques.data]);

  if (!cible) return null;
  const libres = (boutiques.data ?? []).filter((b) => !b.easyshopTenantId);

  const valider = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(
        "/platform/administration/easyshop/links",
        mode === "existing"
          ? { mode, tenantId: cible.tenantId, boutiqueId }
          : { mode, tenantId: cible.tenantId, organizationId, name, city, type }
      );
      onDone(`${cible.name} est liée. Lancez « Reprendre l'historique » pour rapatrier ses 13 derniers mois.`);
      onClose();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open onClose={onClose} width={460}>
      <DrawerHeader title={`Lier « ${cible.name} »`} onClose={onClose} />
      <div className="text-[12.5px] leading-[1.5] text-muted">
        Ses chiffres viendront désormais de la caisse Easy Shop. Les actions propres à Hiboutik (prix, arrivages, détail des
        tickets) ne s&apos;appliquent pas à une boutique alimentée par la caisse.
      </div>
      <Tabs<"existing" | "new">
        tabs={[
          { key: "existing", label: "Boutique existante" },
          { key: "new", label: "Nouvelle boutique" },
        ]}
        value={mode}
        onChange={setMode}
      />
      {boutiques.isLoading && <Loading />}
      {mode === "existing" ? (
        <label>
          <FieldLabel>Boutique du tableau de bord</FieldLabel>
          <select className={inputClass} value={boutiqueId} onChange={(e) => setBoutiqueId(e.target.value)}>
            <option value="">Choisir…</option>
            {libres.map((b) => (
              <option key={b.id} value={b.id}>
                {b.organization.name} — {b.name}
                {b.id === cible.suggestion?.boutiqueId ? " (même compte et magasin Hiboutik)" : ""}
              </option>
            ))}
          </select>
          <div className="mt-[6px] text-[11.5px] text-muted-2">
            Son historique Hiboutik reste en base ; les journées couvertes par la caisse sont réécrites avec ses chiffres.
          </div>
        </label>
      ) : (
        <div className="flex flex-col gap-3">
          <label>
            <FieldLabel>Réseau</FieldLabel>
            <select className={inputClass} value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
              <option value="">Choisir…</option>
              {reseaux.map(([id, n]) => (
                <option key={id} value={id}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label>
            <FieldLabel>Nom affiché</FieldLabel>
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <FieldLabel>Ville</FieldLabel>
            <input className={inputClass} value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>
            <FieldLabel>Type</FieldLabel>
            <select className={inputClass} value={type} onChange={(e) => setType(e.target.value as "INTEGRATED" | "FRANCHISE")}>
              <option value="FRANCHISE">Franchise</option>
              <option value="INTEGRATED">Boutique intégrée</option>
            </select>
          </label>
        </div>
      )}
      {err != null && <ErrorState error={err} />}
      <div className="flex gap-2">
        <Button
          onClick={() => void valider()}
          disabled={busy || (mode === "existing" ? !boutiqueId : !organizationId || !name.trim() || !city.trim())}
        >
          {busy ? "Liaison…" : "Lier la boutique"}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Annuler
        </Button>
      </div>
    </Drawer>
  );
}
